import { DateRange, DateRangePickerProps } from '@mui/x-date-pickers-pro';
import { formatDate } from '@procureai/util';
import { parse, isValid } from 'date-fns/fp';
import { useCallback, useMemo } from 'react';

import { LocationSearchParams } from '~/common/types';
import { useLocationSearchParams } from '~/hooks';

const parseDate = parse(new Date(), 'yyyy-MM-dd');

interface SearchParams extends LocationSearchParams {
  from?: string;
  to?: string;
}

export interface UseUserAvailabilityListResult {
  dateRange: DateRangePickerProps<Date>['value'];
  onDateRangeChange: NonNullable<DateRangePickerProps<Date>['onChange']>;
}

export const useUserAvailabilityList = (): UseUserAvailabilityListResult => {
  const [{ from, to }, setSearchParam] = useLocationSearchParams<SearchParams>();

  const onDateRangeChange = useCallback<NonNullable<DateRangePickerProps<Date>['onChange']>>((dateRange) => {
    const [fromDate, toDate] = dateRange;
    const nextFrom = fromDate ? formatDate(fromDate) : undefined;
    const nextTo = toDate ? formatDate(toDate) : undefined;
    setSearchParam((prev) => ({ ...prev, from: nextFrom, to: nextTo }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dateRange: DateRange<Date> = useMemo(() => {
    const fromParsed = from && parseDate(from);
    const toParsed = to && parseDate(to);
    return [isValid(fromParsed) ? (fromParsed as Date) : null, isValid(toParsed) ? (toParsed as Date) : null];
  }, [from, to]);

  return {
    dateRange,
    onDateRangeChange,
  };
};
